import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from "react";
import { Box, Flex, Spinner } from "theme-ui";

import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import UserContext from "context/User";
import BasicButton from "components/forms/BasicButton";
import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { mnt } from "theme/maintenanceTokens";

import { getPlanEnrichedSitesPage, createClientWorkRequest } from "api/ServicePlans";

/**
 * KNOWN_ISSUES item 18 — the client's "Request work" flow, built from
 * `docs/maintenance-module/mockups/client-plan-self-service.html`.
 *
 * THE ONE IDEA THIS COMPONENT EXISTS TO PROTECT: the client never
 * chooses between a work order and a quote request. That is our
 * vocabulary. They pick a site and say what is wrong; the LOCATION
 * decides where the work lands and the app STATES IT before they send.
 * Hence the coloured block under the picker — it is the feature, not
 * decoration. Do not replace it with a "type of request" selector.
 *
 * SCOPE OF THIS ENTRY POINT. Opened from the plan page, so every site in
 * the picker is already ON the plan (case A of the mockup). The block
 * therefore says so plainly. The off-plan case (B) enters from the
 * client Locations page, which is where a client sees their non-plan
 * sites and where the badge already tells them a site is not covered;
 * case C (a site we do not have) is not built at all. Both are noted in
 * the build spec §7 rather than half-drawn here.
 *
 * The SERVER decides the routing regardless of what this component
 * believes and returns `landedOnPlan`, so the confirmation reflects what
 * actually happened rather than what the UI predicted.
 */

const URGENCY_OPTIONS = [
  { value: "no_rush", label: "No rush" },
  { value: "within_a_month", label: "Within a month" },
  { value: "within_two_weeks", label: "Within two weeks" },
  { value: "urgent", label: "Urgent — safety issue" },
];

const WHAT_OPTIONS = [
  "Mulch is low or thin",
  "Safety concern",
  "Damage after weather",
  "Something else",
];

export default function RequestWorkModal({ planId, planName, presetSite, mode, onClose, onCreated }: any) {
  /**
   * MODE — two intents, one form (Session 76, Jack).
   *
   *   "request" — something is wrong, come and look. The client does not
   *               know the quantity and should not be made to guess.
   *   "order"   — the client knows what the site needs and wants it
   *               priced now, the way they would with an instant
   *               estimate RFQ.
   *
   * This is NOT the choice §1 removed. §1 is about where work LANDS — on
   * the plan or quoted separately — and the location still decides that
   * in both modes. This only changes whether we ask for a quantity up
   * front, which is a question only the client can answer.
   */
  const isOrder = mode === "order";
  /**
   * PRESET MODE (client Locations page). When the caller already knows
   * the site — the client clicked "Request work" on a specific row — the
   * picker is skipped entirely. `presetSite` is
   * `{ locationId, name, city, onPlan, planName }`.
   *
   * `onPlan` drives the copy, and this is the only place the two entry
   * points differ: from the plan page every site is on the plan, from
   * the Locations page a row may not be. The SERVER decides either way
   * and the confirmation reads its answer, so a wrong guess here is a
   * cosmetic pre-send message, never a wrong outcome.
   */
  const preset = presetSite?.locationId ? presetSite : null;

  const [siteSearch, setSiteSearch] = useState("");
  const [siteQuery, setSiteQuery] = useState("");
  const [sites, setSites] = useState<any[]>([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [selected, setSelected] = useState<any>(
    preset ? { locationId: preset.locationId, lcpId: "__preset__", lcpName: preset.name } : null,
  );

  const [what, setWhat] = useState<string>("");
  const [detail, setDetail] = useState("");
  const [urgency, setUrgency] = useState<string>("");
  /**
   * Optional yardage. When the client knows roughly how much mulch the
   * site needs, the request is priced on submit through the same Instant
   * Estimate engine a normal RFQ uses, and they get a quote to approve
   * rather than waiting for someone to price it by hand.
   *
   * Deliberately NOT a required field and NOT a product picker. The
   * client says how much; the plan decides what it is for. Leaving it
   * blank is a perfectly good request and behaves exactly as before.
   */
  const [quantity, setQuantity] = useState<string>("");
  /** Work order fields a client can legitimately fill in. Order mode only. */
  const [onSiteBy, setOnSiteBy] = useState<string>("");
  const [completeBy, setCompleteBy] = useState<string>("");
  const [clientReference, setClientReference] = useState<string>("");
  const [workOrderName, setWorkOrderName] = useState<string>("");
  const [contactName, setContactName] = useState<string>("");
  const [contactPhone, setContactPhone] = useState<string>("");

  /**
   * Mulch calculator availability, for the "not sure how much?" nudge.
   *
   * Gated on the SAME flag as the calculator nav link
   * (`portalLocationsEnabled`, resolved parent-aware because the flag
   * lives on the type-2 organization rather than the individual client
   * record). Pointing someone at a tool they cannot open is worse than
   * not mentioning it.
   */
  const { user } = useContext<any>(UserContext);
  const calculatorEnabled =
    user?.client?.parent?.portalLocationsEnabled === true ||
    (user?.client && !user?.client?.parent
      ? user?.client?.portalLocationsEnabled === true
      : false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<any>(null);

  /**
   * IDEMPOTENCY KEY, generated ONCE per open and held in a ref.
   * Deliberately not regenerated on re-render or retry: a retry after a
   * timeout must carry the SAME token so the server returns the existing
   * request instead of raising a second work order. A new token is only
   * correct for a genuinely new request, which means a new modal.
   * `crypto.randomUUID` is not assumed — older Safari on a client's
   * phone is exactly the browser this will meet.
   */
  const requestToken = useRef(
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`,
  );

  useEffect(() => {
    const t = setTimeout(() => setSiteQuery(siteSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [siteSearch]);

  const loadSites = useCallback(async () => {
    if (!planId) return;
    // Preset mode never opens the picker, so it never pays for the fetch.
    if (preset) {
      setLoadingSites(false);
      return;
    }
    setLoadingSites(true);
    const res: any = await getPlanEnrichedSitesPage({
      data: { id: planId, page: 1, pageSize: 25, search: siteQuery || null },
    });
    setSites(Array.isArray(res?.items) ? res.items : []);
    setLoadingSites(false);
  }, [planId, siteQuery, preset]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const problemDescription = useMemo(() => {
    // In ORDER mode there are no "what is happening" pills — the client
    // already knows what they want, so the description is whatever they
    // chose to add, with a stable prefix so staff reading the queue can
    // see at a glance that this one arrived priced.
    if (isOrder) {
      const parts = [
        `Mulch order — ${quantity || "?"} yd requested by client`,
        detail.trim(),
      ].filter(Boolean);
      return parts.join(" — ");
    }
    const parts = [what, detail.trim()].filter(Boolean);
    return parts.join(" — ");
  }, [isOrder, quantity, what, detail]);

  // ORDER mode requires a quantity — without one there is nothing to
  // price and the whole point of the flow is missed. REQUEST mode
  // requires a description instead, and never asks for a number.
  const canSend = isOrder
    ? !!selected?.locationId && Number(quantity) >= 1 && !submitting
    : !!selected?.locationId && !!problemDescription && !submitting;

  const submit = async () => {
    if (!canSend) return;
    setSubmitting(true);
    setError(null);
    const res: any = await createClientWorkRequest({
      data: {
        planId,
        locationId: selected.locationId,
        problemDescription,
        urgency: urgency || undefined,
        estimatedQuantity:
          Number(quantity) >= 1 ? Number(quantity) : undefined,
        onSiteByAt: onSiteBy || undefined,
        completeByAt: completeBy || undefined,
        clientReference: undefined,
        workOrderNumber: clientReference.trim() || undefined,
        workOrderName: workOrderName.trim() || undefined,
        siteContactName: contactName.trim() || undefined,
        siteContactPhone: contactPhone.trim() || undefined,
        requestToken: requestToken.current,
      },
    });
    setSubmitting(false);
    if (res?.success && res?.id) {
      setDone(res);
      if (typeof onCreated === "function") onCreated(res);
      return;
    }
    // Surface the server's message when there is one. A 403 here is
    // meaningful to staff reading a bug report ("not enabled for this
    // plan" vs "location not available to you") and meaningless noise to
    // the client, so the fallback stays generic.
    //
    // NestJS ValidationPipe returns `message` as an ARRAY of strings, not
    // a string. The original `typeof === "string"` test dropped every
    // validation failure straight into the generic fallback, so a 400
    // naming the exact bad field looked identical to a network outage.
    // That cost a debugging session on 2026-09-14.
    const serverMessage = Array.isArray(res?.message)
      ? res.message.join(" ")
      : typeof res?.message === "string"
        ? res.message
        : null;
    if (!serverMessage) {
      // Nothing usable came back — log the raw payload so the next person
      // has something to read in the console.
      console.error("[RequestWorkModal] request failed, raw response:", res);
    }
    setError(serverMessage || "We could not send that request. Please try again.");
  };

  if (done) {
    return (
      <Modal isOpen={true} toggle={onClose} size="md" centered={true}>
        <ModalHeader toggle={onClose}></ModalHeader>
        <ModalBody>
          <Box sx={{ padding: "4px 6px 10px" }}>
            <H5 fontFamily="Poppins" fontWeight={600} margin={0} fontSize={"17px"}>
              {done.pricedQuoteId
                ? "Your quote is ready"
                : done.pricedRfqId
                ? "We are pricing it now"
                : done.pricingError
                ? "We have it — we will price it by hand"
                : "Request sent"}
            </H5>
            <Subtext fontSize={"13px"} fontFamily={"Poppins"} marginTop={"8px"} marginBottom={0} color={mnt.textMuted}>
              {/* Copy keys off the SERVER's answer, not the UI's guess.
                  A priced request is a different promise from an
                  unpriced one — saying "a quote is on its way" when
                  pricing silently failed would be worse than saying
                  nothing about it. */}
              {done.pricedQuoteId
                ? "It is priced and waiting for you to approve. Nothing starts until you do."
                : done.pricedRfqId
                ? "It is priced. Open it to see the numbers, pick the service you want, and generate your quote — nothing starts until you approve it."
                : done.pricingError
                ? /* They asked for a price and we could not produce one —
                     usually because the site sits outside the depots that
                     can supply it. Saying nothing would leave them
                     expecting a quote that is not coming automatically.
                     The reason itself stays internal; the Slack alert
                     carries it to the team. */
                  "This one needs a quote built by hand rather than priced automatically. Our team has it and will send you a quote to approve — nothing starts until you do."
                : done.landedOnPlan
                ? `We have it. It is on your ${planName || "maintenance"} plan and you will get a quote to approve before any work starts.`
                : "We have it. This site is not on your plan, so we will quote it on its own and invoice it separately."}
            </Subtext>
            {(done.pricedQuoteId || done.pricedRfqId) && (
              <Box sx={{ marginTop: "14px" }}>
                {/* Take them TO it. An RFQ exists at this point with real
                    pricing on it — stopping at "request sent" hides the
                    thing they just asked for and makes them hunt for it.
                    Quote first when there is one (approve it), otherwise
                    the RFQ (choose services, generate the quote) — the
                    same screens they already use for a normal RFQ. */}
                <BasicButton
                  type="button"
                  onClick={() => {
                    window.location.href = done.pricedQuoteId
                      ? `/quotes/${done.pricedQuoteId}`
                      : `/quote-requests/${done.pricedRfqId}`;
                  }}
                >
                  {done.pricedQuoteId
                    ? "View and approve the quote"
                    : "See the pricing and choose your service"}
                </BasicButton>
              </Box>
            )}
            {done.duplicate && (
              <Subtext fontSize={"12px"} fontFamily={"Poppins"} marginTop={"10px"} marginBottom={0} color={mnt.textMuted}>
                This matched a request you had already sent, so we did not raise a second one.
              </Subtext>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <BasicButton type="button" onClick={onClose}>
            Done
          </BasicButton>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered={true}>
      <ModalHeader toggle={onClose}></ModalHeader>
      <ModalBody>
        <Box sx={{ padding: "4px 6px 10px" }}>
          <H5 fontFamily="Poppins" fontWeight={600} margin={0} fontSize={"17px"}>
            {isOrder ? "Create a work order" : "Request work"}
          </H5>
          <Subtext fontSize={"12.5px"} fontFamily={"Poppins"} marginTop={"6px"} marginBottom={"16px"} color={mnt.textMuted}>
            {isOrder
              ? "Tell us the site and how much it needs. We will price it straight away and send you a quote to approve — nothing starts until you do."
              : "Tell us the site and what is happening. We will confirm where it lands before anything starts."}
          </Subtext>

          {/* 1 — the site. The picker runs FIRST because the location is
              what decides everything below it. In preset mode the caller
              already answered this, so the site is stated rather than
              chosen. */}
          <FieldLabel>Which location</FieldLabel>
          {preset ? (
            <Box
              sx={{
                padding: "9px 12px",
                border: "0.5px solid " + mnt.divider,
                borderRadius: "4px",
              }}
            >
              {/* Box, not Span: the Span typography component drops
                  layout styles passed through sx, so `display: block`
                  silently did nothing and the site name ran straight into
                  the city ("214 North ClarkFresno"). Session 76. */}
              <Box
                as="div"
                sx={{ fontFamily: "Poppins", fontSize: "12.5px", fontWeight: 600, color: mnt.ink }}
              >
                {preset.name || "Site"}
              </Box>
              {preset.city && (
                <Box
                  as="div"
                  sx={{ fontFamily: "Poppins", fontSize: "11.5px", color: mnt.textMuted, marginTop: "2px" }}
                >
                  {preset.city}
                </Box>
              )}
            </Box>
          ) : (
            <>
          <input
            type="text"
            value={siteSearch}
            onChange={(e: any) => setSiteSearch(e.target.value)}
            placeholder="Search your sites"
            style={inputStyle}
          />
          <Box
            sx={{
              marginTop: "8px",
              maxHeight: "168px",
              overflowY: "auto",
              border: "0.5px solid " + mnt.divider,
              borderRadius: "4px",
            }}
          >
            {loadingSites ? (
              <Box sx={{ textAlign: "center", paddingY: "22px" }}>
                <Spinner size={20} />
              </Box>
            ) : sites.length === 0 ? (
              <Box sx={{ padding: "18px", textAlign: "center" }}>
                <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                  No sites match that search.
                </Subtext>
              </Box>
            ) : (
              sites.map((s: any, idx: number) => {
                const isSel = selected?.lcpId === s.lcpId;
                return (
                  <Flex
                    key={s.lcpId || idx}
                    onClick={() => setSelected(s)}
                    sx={{
                      padding: "9px 12px",
                      gap: "8px",
                      alignItems: "center",
                      cursor: "pointer",
                      background: isSel ? mnt.bgSubtle : "transparent",
                      borderBottom: idx === sites.length - 1 ? "none" : "0.5px solid " + mnt.divider,
                      "&:hover": { background: mnt.bgSubtle },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        as="div"
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "12.5px",
                          fontWeight: isSel ? 600 : 500,
                          color: mnt.ink,
                        }}
                      >
                        {s.lcpName || s.address?.line1 || "Site"}
                      </Box>
                      {s.city && (
                        <Box
                          as="div"
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "11.5px",
                            color: mnt.textMuted,
                            marginTop: "2px",
                          }}
                        >
                          {s.city}
                        </Box>
                      )}
                    </Box>
                    {isSel && (
                      <Span fontFamily={"Poppins"} fontSize={"11px"} sx={{ color: mnt.link }}>
                        Selected
                      </Span>
                    )}
                  </Flex>
                );
              })
            )}
          </Box>
            </>
          )}

          {/* 2 — WHERE IT LANDS, stated in plain language before they
              send. This block is the whole design. From the plan page
              every site in the picker is on the plan; from the Locations
              page the caller tells us via `presetSite.onPlan`. Either
              way the SERVER decides and the confirmation reads its
              answer, so this can only ever be a cosmetic mismatch. */}
          {selected && (
            <Box
              sx={{
                marginTop: "12px",
                padding: "11px 13px",
                borderRadius: "4px",
                background: mnt.bgSubtle,
                borderLeft: "3px solid " + (preset && preset.onPlan === false ? mnt.amber : mnt.link),
              }}
            >
              {preset && preset.onPlan === false ? (
                <>
                  <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "12.5px", fontWeight: 600, color: mnt.ink }}>
                    This one is separate from your plan
                  </Box>
                  <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "12px", color: mnt.textMuted, marginTop: "4px" }}>
                    {preset.name || "This site"} is not on your{planName ? ` ${planName}` : ""} plan, so we will quote it on its own and invoice it separately. It will not count toward your plan.
                  </Box>
                </>
              ) : (
                <>
                  <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "12.5px", fontWeight: 600, color: mnt.ink }}>
                    This goes on your {preset?.planName || planName || "maintenance"} plan
                  </Box>
                  <Box as="div" sx={{ fontFamily: "Poppins", fontSize: "12px", color: mnt.textMuted, marginTop: "4px" }}>
                    Covered by your plan pricing, and it counts toward this season. You will get a quote to approve before any work starts.
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* 3 — what is happening. REQUEST MODE ONLY. In order mode the
              client already knows what they want; asking them to pick a
              symptom is noise. */}
          {!isOrder && (
            <Box sx={{ marginTop: "16px" }}>
              <FieldLabel>What is happening</FieldLabel>
              <Flex sx={{ gap: "6px", flexWrap: "wrap" }}>
                {WHAT_OPTIONS.map((opt) => (
                  <ChoiceChip key={opt} label={opt} active={what === opt} onClick={() => setWhat(opt)} />
                ))}
              </Flex>
            </Box>
          )}

          {/* 4 — when. ADVISORY. The server stores this for staff to read
              and never lets it set an SLA or queue rank, so nothing here
              should imply a committed date. */}
          <Box sx={{ marginTop: "14px" }}>
            <FieldLabel>When do you need it</FieldLabel>
            <Flex sx={{ gap: "6px", flexWrap: "wrap" }}>
              {URGENCY_OPTIONS.map((opt) => (
                <ChoiceChip
                  key={opt.value}
                  label={opt.label}
                  active={urgency === opt.value}
                  onClick={() => setUrgency(opt.value)}
                />
              ))}
            </Flex>
          </Box>

          {/* 4b — quantity. THE question in order mode, where it is
              required and sits first. In request mode it is an optional
              shortcut for a client who happens to know the number. Only
              offered when the site is ON the plan: off-plan work has no
              plan product to price against. */}
          {(!preset || preset.onPlan !== false) && (
            <Box sx={{ marginTop: "14px" }}>
              <FieldLabel>
                {isOrder
                  ? "How much mulch does it need?"
                  : "How much do you think it needs? (optional)"}
              </FieldLabel>
              <Flex sx={{ alignItems: "center", gap: "8px", marginTop: "6px" }}>
                <input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e: any) => setQuantity(e.target.value)}
                  placeholder="e.g. 110"
                  style={{
                    width: "120px",
                    padding: "8px 10px",
                    border: "0.5px solid " + mnt.divider,
                    borderRadius: "4px",
                    fontFamily: "Poppins",
                    fontSize: "12.5px",
                    color: mnt.ink,
                  }}
                />
                <Box as="span" sx={{ fontFamily: "Poppins", fontSize: "12px", color: mnt.textMuted }}>
                  cubic yards
                </Box>
              </Flex>
              <Box
                as="div"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "11.5px",
                  color: mnt.textMuted,
                  marginTop: "6px",
                  lineHeight: 1.5,
                }}
              >
                {isOrder
                  ? "We will price this amount and send a quote to approve."
                  : "Tell us roughly how much and we will price it straight away, so you get a quote to approve instead of waiting. Not sure? Leave it blank and we will work it out from the site."}
              </Box>

              {/* THE CALCULATOR PROMPT. Its own block, not a clause buried
                  in helper text — a client who does not know the number is
                  stuck at this field, and this is the way out. Shown only
                  when the calculator is actually enabled for their org,
                  and only while the box is empty, so it disappears the
                  moment it stops being useful. */}
              {calculatorEnabled && !(Number(quantity) >= 1) && (
                <Flex
                  sx={{
                    marginTop: "10px",
                    padding: "10px 12px",
                    borderRadius: "5px",
                    background: mnt.bgSubtle,
                    border: "0.5px solid " + mnt.divider,
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    as="div"
                    sx={{ fontFamily: "Poppins", fontSize: "11.5px", color: mnt.ink }}
                  >
                    Not sure how much this playground needs?
                  </Box>
                  {/* DEEP LINK TO THE SITE'S MAP, not the calculator
                      landing. Sending them to the landing meant they had
                      to remember or re-copy the address they had just
                      picked, type it into a search box, and geocode a
                      site the app already knows — work the app created
                      for them. `/:locationId/edit` is the same alias the
                      operator Location page uses: it starts a fresh map
                      at that site (or opens the most recent one) and
                      backfills coords from the address when the site was
                      imported without them. Falls back to the landing
                      only when no site is picked yet, which is the one
                      case where there is nothing to deep-link to.

                      New tab on purpose: the modal stays open behind it,
                      so the number can be typed straight in on return. */}
                  <a
                    href={
                      selected?.locationId
                        ? `/mulch-calculator/${selected.locationId}/edit`
                        : "/mulch-calculator"
                    }
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      fontFamily: "Poppins",
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: "#fff",
                      background: mnt.ink,
                      padding: "6px 12px",
                      borderRadius: "4px",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Measure it in the mulch calculator
                  </a>
                </Flex>
              )}
            </Box>
          )}

          {/* 4c — THE WORK ORDER FIELDS. Order mode only. These are the
              same fields a Corrigo work order carries, limited to what a
              client actually knows: dates they need, their own reference,
              and who is on site. NTE and priority are deliberately absent
              — a client setting an NTE would be setting our ceiling. */}
          {isOrder && (
            <Box sx={{ marginTop: "16px" }}>
              <Flex sx={{ gap: "12px", flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 180px" }}>
                  <FieldLabel>Needed on site by</FieldLabel>
                  <input
                    type="date"
                    value={onSiteBy}
                    onChange={(e: any) => setOnSiteBy(e.target.value)}
                    style={{
                      width: "100%",
                      marginTop: "6px",
                      padding: "8px 10px",
                      border: "0.5px solid " + mnt.divider,
                      borderRadius: "4px",
                      fontFamily: "Poppins",
                      fontSize: "12.5px",
                      color: mnt.ink,
                    }}
                  />
                </Box>
                <Box sx={{ flex: "1 1 180px" }}>
                  <FieldLabel>Complete by</FieldLabel>
                  <input
                    type="date"
                    value={completeBy}
                    onChange={(e: any) => setCompleteBy(e.target.value)}
                    style={{
                      width: "100%",
                      marginTop: "6px",
                      padding: "8px 10px",
                      border: "0.5px solid " + mnt.divider,
                      borderRadius: "4px",
                      fontFamily: "Poppins",
                      fontSize: "12.5px",
                      color: mnt.ink,
                    }}
                  />
                </Box>
              </Flex>

              <Flex sx={{ gap: "12px", flexWrap: "wrap", marginTop: "12px" }}>
                <Box sx={{ flex: "1 1 180px" }}>
                  <FieldLabel>Work order number (optional)</FieldLabel>
                  <input
                    type="text"
                    value={clientReference}
                    onChange={(e: any) => setClientReference(e.target.value)}
                    placeholder="Your own number, if you use one"
                    style={{
                      width: "100%",
                      marginTop: "6px",
                      padding: "8px 10px",
                      border: "0.5px solid " + mnt.divider,
                      borderRadius: "4px",
                      fontFamily: "Poppins",
                      fontSize: "12.5px",
                      color: mnt.ink,
                    }}
                  />
                </Box>
                <Box sx={{ flex: "1 1 180px" }}>
                  <FieldLabel>Work order name (optional)</FieldLabel>
                  <input
                    type="text"
                    value={workOrderName}
                    onChange={(e: any) => setWorkOrderName(e.target.value)}
                    placeholder="What to call this one"
                    style={{
                      width: "100%",
                      marginTop: "6px",
                      padding: "8px 10px",
                      border: "0.5px solid " + mnt.divider,
                      borderRadius: "4px",
                      fontFamily: "Poppins",
                      fontSize: "12.5px",
                      color: mnt.ink,
                    }}
                  />
                </Box>
              </Flex>

              <Flex sx={{ gap: "12px", flexWrap: "wrap", marginTop: "12px" }}>
                <Box sx={{ flex: "1 1 180px" }}>
                  <FieldLabel>Site contact (optional)</FieldLabel>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e: any) => setContactName(e.target.value)}
                    placeholder="Who the crew asks for"
                    style={{
                      width: "100%",
                      marginTop: "6px",
                      padding: "8px 10px",
                      border: "0.5px solid " + mnt.divider,
                      borderRadius: "4px",
                      fontFamily: "Poppins",
                      fontSize: "12.5px",
                      color: mnt.ink,
                    }}
                  />
                </Box>
                <Box sx={{ flex: "1 1 180px" }}>
                  <FieldLabel>Contact phone (optional)</FieldLabel>
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e: any) => setContactPhone(e.target.value)}
                    style={{
                      width: "100%",
                      marginTop: "6px",
                      padding: "8px 10px",
                      border: "0.5px solid " + mnt.divider,
                      borderRadius: "4px",
                      fontFamily: "Poppins",
                      fontSize: "12.5px",
                      color: mnt.ink,
                    }}
                  />
                </Box>
              </Flex>
            </Box>
          )}

          {/* 5 — free text */}
          <Box sx={{ marginTop: "14px" }}>
            <FieldLabel>{isOrder ? "Anything we should know? (optional)" : "Tell us more"}</FieldLabel>
            <textarea
              value={detail}
              onChange={(e: any) => setDetail(e.target.value)}
              rows={3}
              maxLength={1800}
              placeholder="Anything that helps us price it — which playground, how much of it, access notes"
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </Box>

          {error && (
            <Box sx={{ marginTop: "12px" }}>
              <Span fontFamily={"Poppins"} fontSize={"12px"} sx={{ color: mnt.red }}>
                {error}
              </Span>
            </Box>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <BasicButton type="button" outline={true} scheme="secondary" onClick={onClose} styles={{ margin: "0px 10px .25rem 0" }}>
          Cancel
        </BasicButton>
        <BasicButton type="button" onClick={submit} $submitting={submitting} disabled={!canSend}>
          {isOrder ? "Get my price" : "Send request"}
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
}

const inputStyle: any = {
  width: "100%",
  padding: "8px 10px",
  fontFamily: "Poppins",
  fontSize: "12.5px",
  color: mnt.ink,
  border: "0.5px solid " + mnt.divider,
  borderRadius: "4px",
  outline: "none",
  boxSizing: "border-box",
};

function FieldLabel({ children }: any) {
  return (
    <Span
      fontFamily={"Poppins"}
      fontSize={"11px"}
      sx={{ display: "block", marginBottom: "5px", fontWeight: 600, color: mnt.textMuted, letterSpacing: "0.02em" }}
    >
      {children}
    </Span>
  );
}

/**
 * Plain `<button>` with inline styles, NOT `Box as="button"` — the
 * theme-ui prop trap documented in SESSION_STATE (Session 70, item 17).
 */
function ChoiceChip({ label, active, onClick }: any) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: "Poppins",
        fontSize: "12px",
        padding: "6px 11px",
        borderRadius: "14px",
        cursor: "pointer",
        background: active ? mnt.bgSubtle : "#fff",
        border: "1px solid " + (active ? mnt.link : mnt.divider),
        color: active ? mnt.link : mnt.ink,
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}
